/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {Component, Inject} from '@angular/core';
import * as _ from 'lodash';

import template from './site-list-page.component.html';
import {AdminListColumn, AdminResourceListPageBase} from '../common/controller/admin-resource-list-page-base';
import {getListChildren} from '../common/controller/resource-list-utils';
import {OBJECT_TYPE} from '../common/constants/object-type';
import {SiteBulkActionService} from './site-bulk-action.service';
import {AdUnitResource} from '../../components/api/resources/resource/adunit.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';

@Component({
  standalone: false,
  selector: 'adn-site-list-page',
  template
})
export class SiteListPageComponent extends AdminResourceListPageBase {
  readonly itemStateName = 'app.inv.sites.site';
  readonly adUnitItemStateName = 'app.inv.ad-units.ad-unit';
  readonly siteGroupItemStateName = 'app.inv.site-groups.site-group';
  readonly earningsAccountItemStateName = 'app.inv.earnings-accounts.earnings-account';

  readonly childColumns: AdminListColumn[] = [
    {param: 'name'},
    {param: '', label: 'dimensions', sortable: false},
    {param: 'description'},
    {param: '', label: 'tagId', sortable: false}
  ];

  countryPopoverOpen = false;
  countryWorking: string[] = [];

  constructor(
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    private readonly adnListHelper: AdnListHelperService,
    @Inject(AdUnitResource) private readonly AdUnit: any,
    private readonly siteBulkActionService: SiteBulkActionService
  ) {
    super(LocalNetworkProfile);
  }

  get columns(): AdminListColumn[] {
    return this.normalizeColumns(this.sortHeaderParams);
  }

  get sortHeaderParams(): any[] {
    return this.list?.siteListSortParams || [];
  }

  get showMerchantColumn(): boolean {
    return !!this.list?.showMerchantColumn;
  }

  filtersResolved(): boolean {
    const list = this.list;
    if (!list) {
      return true;
    }
    return !!(list.selectInQuery?.$resolved
      && list.dateQuery?.$resolved
      && list.targetQuery?.$resolved
      && list.teamQuery?.$resolved);
  }

  merchantSummaryForSite(site: any): string {
    return this.list?.merchantSummaryForSite?.(site) || '';
  }

  getName(code: string): string {
    return this.list?.getName?.(code) || '';
  }

  selectedCountryNames(): string[] {
    const selected = this.list?.selectIn?.country || {};
    return _.map(_.keys(selected), (key) => {
      return selected[key] ? this.getName(key) : null;
    }).filter((name) => !!name);
  }

  toggleCountryPopover(): void {
    this.countryPopoverOpen = !this.countryPopoverOpen;
    if (this.countryPopoverOpen) {
      const selected = this.list?.selectIn?.country || {};
      this.countryWorking = _.keys(_.pickBy(selected, (val) => !!val));
    }
  }

  isCountryWorkingSelected(code: string): boolean {
    return (this.countryWorking || []).indexOf(code) > -1;
  }

  toggleCountryWorking(code: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const working = [...(this.countryWorking || [])];
    const index = working.indexOf(code);
    if (checked && index < 0) {
      working.push(code);
    } else if (!checked && index > -1) {
      working.splice(index, 1);
    }
    this.countryWorking = working;
  }

  applyCountryFilter(): void {
    this.list?.makeSelectInQuery?.('country', this.countryWorking, this.list.allCountries?.length);
    this.countryPopoverOpen = false;
  }

  clearCountryFilter(): void {
    this.list?.makeSelectInQuery?.('country', null);
    this.countryPopoverOpen = false;
    this.countryWorking = [];
  }

  earningsAccountLinkDisabled(): boolean {
    return this.list?.perms?.MANAGE_BUSINESS !== true;
  }

  siteGroupLinkDisabled(): boolean {
    return this.list?.perms?.MANAGE_SYSTEM !== true;
  }

  toggleChildren(site: any): void {
    this.list.getChildren?.(site);
  }

  childList(siteId: string): any {
    return this.list.children?.[siteId];
  }

  copyTagId(tagId: string): void {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(tagId);
    }
  }

  protected afterListInit(): void {
    if (!this.list) {
      return;
    }
    this.list.canExpand = true;
    if (!this.list.getChildren) {
      getListChildren(this.list, this.adnListHelper, OBJECT_TYPE.Site, this.AdUnit);
    }
    if (!this.list.bulkAction) {
      this.list.bulkAction = this.siteBulkActionService.createBulkAction(this.list);
    }
  }
}
